import {
  AiAllowedModelsSchema,
  AiGatewayModelSchema,
  AiModelEffortSchema,
  AiModelUseCaseSchema,
} from "@jurni/schemas/ai/ai-models";
import * as z from "zod";
import { baseContract } from "./base";
import { ACCESS_LEVEL } from "./metadata";
import {
  BillingCycleSchema,
  BillingEntitlementSchema,
  BillingPlanBenefitSchema,
  ManualBillingCalculationInputSchema,
} from "@jurni/schemas/primitives/billing";
import { DomainSchema } from "@jurni/schemas/primitives/domain";
import { HostnameSchema, HOSTNAME_PATTERN } from "@jurni/schemas/primitives/hostname";
import { ShortUrlSchema } from "@jurni/schemas/primitives/short-url";
import { CurrencyCodeSchema } from "@jurni/schemas/primitives/currency-code";
import { CreateAdminShopInputSchema } from "@jurni/schemas/backoffice/manual-store";
import { ShopRegionSchema } from "@jurni/schemas/enums/shop-region";
import { StorefrontFallbackInputSchema, StorefrontFallbackSchema } from "@jurni/schemas/storefront/storefront-fallback";
// Reuse the existing schema for the linked-product rows — same shape as our
// admin endpoint returns. The `data` blob stays typed as `z.unknown()` in the
// admin output because Prisma returns it as JsonValue (which is wider than
// VirtualProductDataSchema's object shape) and forcing a typed parse on every
// read would add cost without benefit. The schemas package's data schema is
// still useful on the write/validate side (input patches).
import { VirtualProductProductsSchema } from "./schemas/virtual-products";
import { adminInspirationRouterContract } from "./admin-inspiration";
import { adminMetaAttributionRouterContract } from "./admin-meta-attribution";
import { adminStorefrontRouterContract } from "./admin-storefront";
import { adminAnalyticsRouterContract } from "./admin-analytics";
import { adminFinancialRouterContract, adminShopUsageChargesContract } from "./admin-financial";
import { adminSystemConfigRouterContract } from "./admin-system-config";
import { removeAdminShopifyWebhooksContract } from "./admin-shopify-webhooks";

// Shared primitives ---------------------------------------------------------
const customerIdParam = z.object({ customerId: z.coerce.number().int().positive() });
const shopIdParam = z.object({ shopId: z.coerce.number().int().positive() });
const productIdParam = customerIdParam.extend({ productId: z.coerce.number().int().positive() });

const ShopStatusSchema = z.enum(["ACTIVE", "PENDING", "DISABLED"]);

// Flag identifier
const FLAG_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_.-]{0,63}$/;

// Hostnames are case-insensitive. Storing them lowercase lets every lookup match exactly.
const SubdomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Required")
  .max(253, "Too long (max 253 chars)")
  .regex(HOSTNAME_PATTERN, "Must be a valid hostname (e.g. hoka.myjurni.shop)");

const NullableUrlSchema = ShortUrlSchema.nullable();
const NullableStringSchema = (max: number) => z.string().trim().max(max, `Too long (max ${max} chars)`).nullable();
const TrimmedStringSchema = (min: number, max: number) =>
  z.string().trim().min(min, "Required").max(max, `Too long (max ${max} chars)`);

const ProductDataSchema = z.unknown().nullable();
const ShopJsonBlobSchema = z.unknown().nullable();

// ---- users.list ------------------------------------------------------------
const UserRoleSchema = z.enum(["admin", "user", "designer", "superDesigner"]);

export const listAdminUsersContract = baseContract
  .route({ method: "GET", path: "/admin/users" })
  .meta({
    accessLevel: ACCESS_LEVEL.ADMIN,
    permissions: { basic: ["manage"] },
  })
  .input(
    z
      .object({
        limit: z.coerce.number().int().min(1).max(100).optional(),
        offset: z.coerce.number().int().min(0).default(0),
        search: z.string().trim().max(100).optional(),
      })
      .optional(),
  )
  .output(
    z.object({
      users: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          email: z.string(),
          role: UserRoleSchema,
          status: z.string(),
          banned: z.boolean().nullable(),
          shopCount: z.number().int().min(0),
          createdAt: z.string().datetime(),
        }),
      ),
      total: z.number().int().min(0),
    }),
  );

// ---- users.get -------------------------------------------------------------
const userIdParam = z.object({ userId: z.string().trim().min(1) });

const UserRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  role: UserRoleSchema,
  status: z.string(),
  banned: z.boolean().nullable(),
  banReason: z.string().nullable(),
  banExpires: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  shops: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      domain: z.string(),
      assignedAt: z.string().datetime(),
      organizationId: z.string(),
      organizationName: z.string(),
      hasOrganizationMembership: z.boolean(),
    }),
  ),
});

export const getAdminUserContract = baseContract
  .route({ method: "GET", path: "/admin/users/{userId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(userIdParam)
  .output(UserRecordSchema);

// ---- users.update -----------------------------------------------------------
const UserUpdatePatchSchema = z
  .object({
    name: TrimmedStringSchema(1, 200).optional(),
    email: z.string().trim().email("Must be a valid email").max(320, "Too long (max 320 chars)").optional(),
    role: UserRoleSchema.optional(),
    status: TrimmedStringSchema(1, 50).optional(),
    emailVerified: z.boolean().optional(),
    image: NullableUrlSchema.optional(),
    banned: z.boolean().optional(),
    banReason: NullableStringSchema(500).optional(),
    banExpires: z.string().datetime().nullable().optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Patch must include at least one field" });

export const updateAdminUserContract = baseContract
  .route({ method: "PATCH", path: "/admin/users/{userId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(userIdParam.extend({ patch: UserUpdatePatchSchema }))
  .output(UserRecordSchema);

// ---- users.repairOrganizationMembership -----------------------------------
export const repairAdminUserOrganizationMembershipContract = baseContract
  .route({ method: "POST", path: "/admin/users/{userId}/organization-memberships/repair" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    userIdParam.extend({
      shopId: z.coerce.number().int().positive(),
      role: z.enum(["owner", "staff", "admin", "designer"]),
    }),
  )
  .output(UserRecordSchema);

// ---- shops.list ----------------------------------------------------------
export const getAdminShopsContract = baseContract
  .route({ method: "GET", path: "/admin/shops" })
  .meta({
    accessLevel: ACCESS_LEVEL.ADMIN,
    permissions: { basic: ["manage"] },
  })
  .input(
    z
      .object({
        limit: z.coerce.number().int().min(1).max(100).optional(),
        offset: z.coerce.number().int().min(0).default(0),
        search: z.string().trim().max(100).optional(),
      })
      .optional(),
  )
  .output(
    z.object({
      shops: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          domain: z.string(),
          status: ShopStatusSchema,
        }),
      ),
      total: z.number().int().min(0),
    }),
  );

export const getAllAdminShopsContract = baseContract
  .route({ method: "GET", path: "/admin/backoffice/shops" })
  .meta({
    accessLevel: ACCESS_LEVEL.ADMIN,
    permissions: { basic: ["manage"] },
  })
  .input(
    z
      .object({
        limit: z.coerce.number().int().min(1).max(100).optional(),
        offset: z.coerce.number().int().min(0).default(0),
        search: z.string().trim().max(100).optional(),
        status: ShopStatusSchema.optional(),
      })
      .optional(),
  )
  .output(
    z.object({
      shops: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          domain: z.string(),
          status: ShopStatusSchema,
        }),
      ),
      total: z.number().int().min(0),
    }),
  );

// ---- shops.get -----------------------------------------------------------
const ShopRecordSchema = z.object({
  id: z.number(),
  name: z.string(),
  url: z.string(),
  domain: z.string(),
  region: ShopRegionSchema,
  subdomain: z.string().nullable(),
  shopType: z.string(),
  shopifyId: z.string(),
  defaultCurrency: z.string(),
  status: ShopStatusSchema,
  organizationId: z.string(),
  vertical: z.string().nullable(),
  termsAndConditionURL: z.string(),
  emailName: z.string().nullable(),
  logo: z.string().nullable(),
  favicon: z.string().nullable(),
  creditIssuePolicy: z.number().int(),
  deleted: z.boolean(),
  brandTokens: z.unknown().nullable(),
  customFonts: z.unknown().nullable(),
  defaultTheme: z.unknown().nullable(),
  productPageConfig: z.unknown().nullable(),
  subdomainConfig: z.unknown().nullable(),
  config: z.unknown(),
});

export const getAdminShopContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(ShopRecordSchema);

// ---- shops.create ----------------------------------------------------------
export const createAdminShopContract = baseContract
  .route({ method: "POST", path: "/admin/shops" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(CreateAdminShopInputSchema)
  .output(ShopRecordSchema);

// ---- shops.brandDesign ---------------------------------------------------
export const getAdminShopBrandDesignContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{shopId}/brand-design" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(shopIdParam)
  .output(
    z.object({
      available: z.boolean(),
      documentUrl: z.string().url().nullable(),
      content: z.string().nullable(),
      readError: z.string().nullable(),
    }),
  );

export const startAdminShopBrandDesignContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{shopId}/brand-design/start" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    shopIdParam.extend({
      confirmation: z.literal("OVERRIDE_CURRENT_BRANDING"),
    }),
  )
  .output(
    z.object({
      workflowId: z.string(),
      temporalRunId: z.string(),
    }),
  );

// ---- shops.update --------------------------------------------------------
const ShopUpdatePatchSchema = z
  .object({
    name: TrimmedStringSchema(1, 200).optional(),
    url: ShortUrlSchema.optional(),
    domain: HostnameSchema.optional(),
    region: ShopRegionSchema.optional(),
    subdomain: SubdomainSchema.nullable().optional(),
    defaultCurrency: CurrencyCodeSchema.optional(),
    vertical: NullableStringSchema(100).optional(),
    termsAndConditionURL: z.string().trim().max(2048).optional(),
    emailName: NullableStringSchema(100).optional(),
    logo: NullableUrlSchema.optional(),
    favicon: NullableUrlSchema.optional(),
    status: ShopStatusSchema.optional(),
    deleted: z.boolean().optional(),
    creditIssuePolicy: z.number().int().min(0).max(365).optional(),
    brandTokens: ShopJsonBlobSchema.optional(),
    customFonts: ShopJsonBlobSchema.optional(),
    defaultTheme: ShopJsonBlobSchema.optional(),
    productPageConfig: ShopJsonBlobSchema.optional(),
    subdomainConfig: ShopJsonBlobSchema.optional(),
    config: z.unknown().optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Patch must include at least one field" });

export const updateAdminShopContract = baseContract
  .route({ method: "PATCH", path: "/admin/shops/{customerId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ patch: ShopUpdatePatchSchema }))
  .output(ShopRecordSchema);

// ---- shops.domains -------------------------------------------------------
export const createAdminShopDomainContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{customerId}/domains" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ hostname: HostnameSchema }))
  .output(DomainSchema);

// ---- shops.customHeadlessConfigs ------------------------------------------
const CustomHeadlessConfigRecordSchema = z.object({
  hostname: HostnameSchema,
  key: z.string(),
  exists: z.boolean(),
  content: z.string().nullable(),
  validationError: z.string().nullable(),
  readError: z.string().nullable(),
});

const CustomHeadlessConfigViewSchema = z.object({
  hostname: HostnameSchema,
  rootDomain: HostnameSchema.nullable(),
  sources: z.array(z.enum(["SHOP_URL", "DOMAIN_TABLE"])),
  exactConfig: CustomHeadlessConfigRecordSchema,
  rootConfig: CustomHeadlessConfigRecordSchema.nullable(),
  effectiveSource: z.enum(["exact", "root", "none"]),
});

const CustomHeadlessConfigContentSchema = z.string().max(1024 * 1024, "custom.json is too large");

export const listAdminShopCustomHeadlessConfigsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/custom-headless-configs" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(
    z.object({
      domains: z.array(CustomHeadlessConfigViewSchema),
      roots: z.array(CustomHeadlessConfigRecordSchema),
    }),
  );

export const getAdminShopCustomHeadlessConfigContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/custom-headless-configs/entry" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ hostname: HostnameSchema }))
  .output(CustomHeadlessConfigViewSchema);

export const updateAdminShopCustomHeadlessConfigContract = baseContract
  .route({ method: "PUT", path: "/admin/shops/{customerId}/custom-headless-configs/entry" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      hostname: HostnameSchema,
      content: CustomHeadlessConfigContentSchema,
    }),
  )
  .output(
    z.object({
      config: CustomHeadlessConfigRecordSchema,
      cdnPropagationDelayed: z.literal(true),
    }),
  );

export const updateAdminShopGeneralCustomHeadlessConfigContract = baseContract
  .route({ method: "PUT", path: "/admin/shops/{customerId}/custom-headless-configs/general" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ content: CustomHeadlessConfigContentSchema }))
  .output(
    z.object({
      results: z.array(
        z.object({
          hostname: HostnameSchema,
          key: z.string(),
          success: z.boolean(),
          config: CustomHeadlessConfigRecordSchema.nullable(),
          error: z.string().nullable(),
        }),
      ),
      cdnPropagationDelayed: z.literal(true),
    }),
  );

// ---- shops.storefrontFallbacks -------------------------------------------
export const listAdminShopStorefrontFallbacksContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/storefront-fallbacks" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(z.object({ fallbacks: z.array(StorefrontFallbackSchema) }));

export const createAdminShopStorefrontFallbackContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{customerId}/storefront-fallbacks" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend(StorefrontFallbackInputSchema.shape))
  .output(StorefrontFallbackSchema);

export const updateAdminShopStorefrontFallbackContract = baseContract
  .route({ method: "PUT", path: "/admin/shops/{customerId}/storefront-fallbacks/{id}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      ...StorefrontFallbackInputSchema.shape,
      id: z.coerce.number().int().positive(),
    }),
  )
  .output(StorefrontFallbackSchema);

export const deleteAdminShopStorefrontFallbackContract = baseContract
  .route({ method: "DELETE", path: "/admin/shops/{customerId}/storefront-fallbacks/{id}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ id: z.coerce.number().int().positive() }))
  .output(z.object({ success: z.boolean() }));

// ---- shops.integrations.get ---------------------------------------------
export const getAdminShopIntegrationsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/integrations" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(
    z.object({
      shopify: z.object({
        connected: z.boolean(),
        domain: z.string(),
        shopifyId: z.string(),
        hasStorefrontToken: z.boolean(),
      }),
      meta: z.object({
        accountCount: z.number().int().min(0),
        accounts: z.array(
          z.object({
            id: z.number(),
            accountId: z.string(),
            tokenExpireDate: z.string().datetime().nullable(),
          }),
        ),
      }),
      stripe: z.object({
        hasActiveSubscription: z.boolean(),
        subscriptionStatus: z.string().nullable(),
      }),
      generic: z.array(
        z.object({
          id: z.number(),
          type: z.string(),
        }),
      ),
      domains: z.array(z.object({ domain: z.string() })),
    }),
  );

// ---- shops.members.list -------------------------------------------------
export const listAdminShopMembersContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/members" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(
    z.object({
      members: z.array(
        z.object({
          userId: z.string(),
          email: z.string(),
          name: z.string(),
          role: z.string(),
          status: z.string(),
          assignedAt: z.string().datetime(),
        }),
      ),
    }),
  );

// ---- shops.billing.get / update ----------------------------------------
export const getAdminShopBillingContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/billing" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(
    z.object({
      subscription: z
        .object({
          id: z.number(),
          status: z.string(),
          paymentProvider: z.string(),
          endOfPeriod: z.string().datetime().nullable(),
          createdAt: z.string().datetime(),
        })
        .nullable(),
      plan: z
        .object({
          id: z.number(),
          name: z.string(),
          description: z.string().nullable(),
          price: z.number(),
          currencyCode: z.string(),
          interval: z.string(),
          isActive: z.boolean(),
          isTest: z.boolean(),
          trialDays: z.number().int().nullable(),
          entitlements: z.unknown().nullable(),
        })
        .nullable(),
      availablePlans: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          description: z.string().nullable(),
          price: z.number(),
          currencyCode: z.string(),
          interval: z.string(),
          trialDays: z.number().int().nullable(),
          isActive: z.boolean(),
          isTest: z.boolean(),
          shopId: z.number().nullable(),
          // False when a live subscription (PENDING/ACTIVE/FROZEN) references
          // this plan, so the backoffice can gate the edit affordance.
          isEditable: z.boolean(),
          lockedReason: z.string().nullable(),
          // Entitlements and benefits are always required to build a plan; we
          // still allow null on the read path because legacy rows may not have
          // been backfilled yet — the edit form treats null as "must fill in".
          entitlements: z.array(BillingEntitlementSchema).nullable(),
          benefits: z.array(BillingPlanBenefitSchema).nullable(),
        }),
      ),
    }),
  );

export const createAdminShopBillingPlanContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{customerId}/billing/plans" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      plan: z
        .object({
          name: z.string().trim().min(1, "Required").max(200, "Max 200 chars"),
          description: z.string().trim().min(1, "Required").max(1000, "Max 1000 chars"),
          price: z.number().min(0, "Must be >= 0"),
          currencyCode: z
            .string()
            .trim()
            .regex(/^[A-Z]{3}$/, "3-letter ISO 4217 code (e.g. USD)")
            .default("USD"),
          interval: BillingCycleSchema.default("month"),
          trialDays: z.number().int().min(0, "Must be >= 0").nullable().optional(),
          isActive: z.boolean().default(true),
          isTest: z.boolean().default(false),
          entitlements: z.array(BillingEntitlementSchema).min(1, "At least one entitlement is required"),
          benefits: z.array(BillingPlanBenefitSchema).min(1, "At least one benefit is required"),
        })
        .strict(),
    }),
  )
  .output(z.object({ id: z.number() }));

export const updateAdminShopBillingPlanContract = baseContract
  .route({ method: "PATCH", path: "/admin/shops/{customerId}/billing/plans/{planId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      planId: z.coerce.number().int().positive(),
      patch: z
        .object({
          name: z.string().trim().min(1, "Required").max(200, "Max 200 chars").optional(),
          // When present in the patch, description must be non-empty — plans
          // are required to have a description.
          description: z.string().trim().min(1, "Required").max(1000, "Max 1000 chars").optional(),
          price: z.number().min(0, "Must be >= 0").optional(),
          currencyCode: z
            .string()
            .trim()
            .regex(/^[A-Z]{3}$/, "3-letter ISO 4217 code (e.g. USD)")
            .optional(),
          interval: BillingCycleSchema.optional(),
          trialDays: z.number().int().min(0, "Must be >= 0").nullable().optional(),
          isActive: z.boolean().optional(),
          isTest: z.boolean().optional(),
          entitlements: z.array(BillingEntitlementSchema).min(1, "At least one entitlement is required").optional(),
          benefits: z.array(BillingPlanBenefitSchema).min(1, "At least one benefit is required").optional(),
        })
        .strict()
        .refine((p) => Object.keys(p).length > 0, { message: "Patch must include at least one field" }),
    }),
  )
  .output(z.object({ ok: z.literal(true) }));

export const updateAdminShopBillingContract = baseContract
  .route({ method: "PATCH", path: "/admin/shops/{customerId}/billing" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      patch: z
        .object({
          status: z.enum(["PENDING", "ACTIVE", "CANCELLED", "EXPIRED", "DECLINED", "FROZEN"]).optional(),
          endOfPeriod: z.string().datetime().nullable().optional(),
          entitlementsOverride: z.array(BillingEntitlementSchema).optional(),
        })
        .strict()
        .refine((p) => Object.keys(p).length > 0, { message: "Patch must include at least one field" }),
    }),
  )
  .output(z.object({ ok: z.literal(true) }));

// ---- shops.config.flags ------------------------------------------------
const FlagKeySchema = z
  .string()
  .regex(FLAG_KEY_PATTERN, "Flag keys must start with a letter and use only letters, numbers, _, ., -");
const FlagValueSchema = z.union([
  z.boolean(),
  z.string().max(500, "Flag value too long (max 500 chars)"),
  z.number(),
  z.null(),
]);
const FlagsSchema = z.record(FlagKeySchema, FlagValueSchema);

export const getAdminShopFlagsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/config/flags" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(z.object({ flags: FlagsSchema }));

export const updateAdminShopFlagsContract = baseContract
  .route({ method: "PUT", path: "/admin/shops/{customerId}/config/flags" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ flags: FlagsSchema }))
  .output(z.object({ flags: FlagsSchema }));

// ---- shops.config.abDefaults -------------------------------------------
const AbDefaultsSchema = z.object({
  defaultExposurePercent: z.number().min(0).max(100).optional(),
  defaultHoldoutPercent: z.number().min(0).max(100).optional(),
  defaultTrafficAllocation: z.record(z.string(), z.number()).optional(),
});

export const getAdminShopAbDefaultsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/config/ab-defaults" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(z.object({ abDefaults: AbDefaultsSchema }));

export const updateAdminShopAbDefaultsContract = baseContract
  .route({ method: "PUT", path: "/admin/shops/{customerId}/config/ab-defaults" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ abDefaults: AbDefaultsSchema }))
  .output(z.object({ abDefaults: AbDefaultsSchema }));

// ---- systemConfig.ai-model ---------------------------------------------
const EditableAiModelConfigKeySchema = AiModelUseCaseSchema;
const GatewayModelIdSchema = z.string().trim().min(1);

const AiModelSystemConfigEntrySchema = z.object({
  usesDirectOpenAi: z.boolean(),
  key: EditableAiModelConfigKeySchema,
  label: z.string(),
  description: z.string(),
  model: GatewayModelIdSchema.nullable(),
  effort: AiModelEffortSchema.nullable(),
});

export const listAdminAiModelSystemConfigContract = baseContract
  .route({ method: "GET", path: "/admin/system-config/ai-models" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .output(
    z.object({
      configs: z.array(AiModelSystemConfigEntrySchema),
      supportedModels: z.array(GatewayModelIdSchema),
      gatewayModels: z.array(AiGatewayModelSchema),
      models: AiAllowedModelsSchema,
      adminModels: AiAllowedModelsSchema,
      supportedEfforts: z.array(AiModelEffortSchema),
    }),
  );

export const updateAdminAiModelSystemConfigContract = baseContract
  .route({ method: "PUT", path: "/admin/system-config/ai-models/{key}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    z.object({
      key: EditableAiModelConfigKeySchema,
      model: GatewayModelIdSchema,
      effort: AiModelEffortSchema,
    }),
  )
  .output(z.object({ config: AiModelSystemConfigEntrySchema }));

const AiModelAllowedListsSchema = z.object({
  models: AiAllowedModelsSchema,
  adminModels: AiAllowedModelsSchema,
});

export const updateAdminBrainSystemConfigContract = baseContract
  .route({ method: "PUT", path: "/admin/system-config/brain" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(AiModelAllowedListsSchema)
  .output(AiModelAllowedListsSchema);

// ---- shops.products ---------------------------------------------------
const ProductRowSchema = z.object({
  id: z.number(),
  name: z.string(),
  url: z.string(),
  description: z.string(),
  image: z.string().nullable(),
  originalPrice: z.number(),
  currencySymbol: z.string(),
  shopifyProductId: z.string(),
  externalId: z.string(),
});

export const listAdminShopProductsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/products" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      search: z.string().trim().max(100).optional(),
    }),
  )
  .output(
    z.object({
      products: z.array(ProductRowSchema),
      total: z.number().int().min(0),
    }),
  );

const ProductDetailSchema = ProductRowSchema.extend({
  data: z.unknown().nullable(),
});

export const getAdminShopProductContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/products/{productId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(productIdParam)
  .output(ProductDetailSchema);

const ProductUpdatePatchSchema = z
  .object({
    name: TrimmedStringSchema(1, 500).optional(),
    url: ShortUrlSchema.optional(),
    description: z.string().max(50_000, "Description too long (max 50,000 chars)").optional(),
    image: NullableUrlSchema.optional(),
    originalPrice: z.number().min(0, "Cannot be negative").max(999_999.99, "Price too high").optional(),
    currencySymbol: z.string().trim().min(1, "Required").max(4, "Currency symbol too long (max 4 chars)").optional(),
    data: ProductDataSchema.optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Patch must include at least one field" });

export const updateAdminShopProductContract = baseContract
  .route({ method: "PATCH", path: "/admin/shops/{customerId}/products/{productId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(productIdParam.extend({ patch: ProductUpdatePatchSchema }))
  .output(ProductDetailSchema);

// ---- shops.products.create (Medusa-backed / MANUAL shops only) -----------
const ProductCurrencyCodeSchema = z.enum(["usd", "eur", "ils"]);

const CreateProductPriceInputSchema = z.object({
  currencyCode: ProductCurrencyCodeSchema,
  amount: z.number().min(0, "Cannot be negative").max(999_999.99, "Price too high"),
});

const CreateProductInputSchema = z.object({
  name: TrimmedStringSchema(1, 500),
  url: ShortUrlSchema,
  description: z.string().trim().max(50_000, "Description too long (max 50,000 chars)").optional(),
  image: ShortUrlSchema.optional(),
  status: z.enum(["draft", "proposed", "published", "rejected"]).default("published"),
  callToActionUrl: ShortUrlSchema.optional(),
  previewUrl: ShortUrlSchema.optional(),
  prices: z.array(CreateProductPriceInputSchema).min(1, "At least one price is required"),
});

export const createAdminShopProductContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{customerId}/products" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ product: CreateProductInputSchema }))
  .output(ProductDetailSchema);

// ---- shops.funnels -----------------------------------------------------
const FunnelStatusInputSchema = z.string().trim().max(50);

const FunnelRowSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  status: z.string(),
  sourceType: z.string(),
  pageCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().nullable(),
});

export const listAdminShopFunnelsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/funnels" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      search: z.string().trim().max(100).optional(),
      status: FunnelStatusInputSchema.optional(),
    }),
  )
  .output(z.object({ funnels: z.array(FunnelRowSchema), total: z.number().int().min(0) }));

const FunnelPageSectionSchema = z.object({
  id: z.number(),
  type: z.string(),
  handle: z.string().nullable(),
  order: z.number().int(),
  templateId: z.number().nullable(),
  data: z.unknown(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const FunnelPageSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  jsx: z.string().nullable(),
  sections: z.array(FunnelPageSectionSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const FunnelVersionSummarySchema = z.object({
  id: z.number(),
  version: z.number().int(),
  publishedAt: z.string().datetime(),
  publishedBy: z.string().nullable(),
  publishedByEmail: z.string().nullable(),
  isCurrentPublished: z.boolean(),
});

export const getAdminShopFunnelContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/funnels/{funnelId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ funnelId: z.coerce.number().int().positive() }))
  .output(
    z.object({
      funnel: FunnelRowSchema.extend({
        defaultPageUrl: z.string().nullable(),
        publishedVersionId: z.number().nullable(),
      }),
      pages: z.array(FunnelPageSchema),
      versions: z.array(FunnelVersionSummarySchema),
    }),
  );

export const updateAdminShopFunnelPageJsxContract = baseContract
  .route({
    method: "PUT",
    path: "/admin/shops/{customerId}/funnels/{funnelId}/pages/{pageId}/jsx",
  })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      funnelId: z.coerce.number().int().positive(),
      pageId: z.coerce.number().int().positive(),
      jsx: z.string().min(1, "JSX cannot be empty"),
    }),
  )
  .output(
    z.object({
      pageId: z.number().int().positive(),
      jsx: z.string(),
      sectionCount: z.number().int().min(0),
      updatedAt: z.iso.datetime(),
    }),
  );

export const copyAdminShopFunnelContract = baseContract
  .route({ method: "POST", path: "/admin/shops/{customerId}/funnels/{funnelId}/copy" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam
      .extend({
        funnelId: z.coerce.number().int().positive(),
        targetCustomerId: z.coerce.number().int().positive(),
        copyMode: z.enum(["BRAIN_REBRAND", "DETERMINISTIC"]).default("BRAIN_REBRAND"),
        /** Full name for the copied funnel. When set, overrides source name + nameSuffix. */
        name: z.string().trim().min(1).max(200).optional(),
        nameSuffix: z.string().trim().max(50).optional(),
      })
      .refine((args) => args.customerId !== args.targetCustomerId, {
        message: "Source and target shop must be different",
        path: ["targetCustomerId"],
      }),
  )
  .output(
    z.object({
      workflowId: z.string(),
      runId: z.string(),
    }),
  );

export const copyStatusAdminShopFunnelContract = baseContract
  .route({
    method: "GET",
    path: "/admin/shops/{customerId}/funnels/copy-status/{workflowId}",
  })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      workflowId: z.string().min(1),
    }),
  )
  .output(
    z.object({
      status: z.string(),
      error: z.string().nullable(),
      result: z
        .object({
          newFunnelId: z.number().int(),
          newFunnelName: z.string(),
          pageCount: z.number().int().min(0),
          sectionCount: z.number().int().min(0),
          copyMode: z.enum(["BRAIN_REBRAND", "DETERMINISTIC"]),
          threadId: z.string().nullable(),
          brainStatus: z.enum(["COMPLETED", "FAILED", "TIMED_OUT", "SKIPPED"]),
          imageCopySummary: z.object({
            requested: z.number().int().min(0),
            uploaded: z.number().int().min(0),
            reused: z.number().int().min(0),
            omitted: z.number().int().min(0),
          }),
        })
        .nullable(),
    }),
  );

export const getAdminShopFunnelVersionContract = baseContract
  .route({
    method: "GET",
    path: "/admin/shops/{customerId}/funnels/{funnelId}/versions/{version}",
  })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      funnelId: z.coerce.number().int().positive(),
      version: z.coerce.number().int().positive(),
    }),
  )
  .output(
    z.object({
      id: z.number(),
      version: z.number().int(),
      publishedAt: z.string().datetime(),
      publishedBy: z.string().nullable(),
      publishedByEmail: z.string().nullable(),
      snapshot: z.unknown(),
    }),
  );

// ---- shops.virtualProducts ---------------------------------------------
// Admin-specific list projection: lifts `title` out of the data blob and adds
// aggregate counts so the list table doesn't have to fetch each VP's data.
const AdminVirtualProductRowSchema = z.object({
  id: z.number(),
  title: z.string().nullable(),
  isGlobal: z.boolean(),
  productCount: z.number().int().min(0),
  campaignCount: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Detail extends the row with the raw `data` blob (kept as unknown — see the
// import comment) and the linked products (typed via the shared schema).
const AdminVirtualProductDetailSchema = AdminVirtualProductRowSchema.extend({
  data: z.unknown().nullable(),
  products: z.array(VirtualProductProductsSchema),
});

export const listAdminShopVirtualProductsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/virtual-products" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      search: z.string().trim().max(100).optional(),
    }),
  )
  .output(
    z.object({
      virtualProducts: z.array(AdminVirtualProductRowSchema),
      total: z.number().int().min(0),
    }),
  );

export const getAdminShopVirtualProductContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/virtual-products/{virtualProductId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ virtualProductId: z.coerce.number().int().positive() }))
  .output(AdminVirtualProductDetailSchema);

// Patch schema mirrors the typed fields on VirtualProductData (in
// ./schemas/virtual-products.ts) with admin-friendly
// validation. The catch-all `data` blob accepts arbitrary JSON for the
// unstructured tail (reviews, specifications, siblingOptions).
const VirtualProductPatchSchema = z
  .object({
    title: z.string().trim().max(500).nullable().optional(),
    subtitle: z.string().trim().max(500).nullable().optional(),
    description: z.string().trim().max(50_000).nullable().optional(),
    images: z.array(z.string().trim().url("Each image must be a valid URL").max(2048)).optional(),
    originalPrice: z.number().min(0).max(999_999.99).optional(),
    compareAtPrice: z.number().min(0).max(999_999.99).nullable().optional(),
    currencySymbol: z.string().trim().min(1).max(4).optional(),
    isGlobal: z.boolean().optional(),
    // Raw `data` blob: when provided, merges with the typed fields above
    // (typed wins). Lets admins tweak reviews/specs/siblingOptions etc.
    data: z.unknown().optional(),
  })
  .strict()
  .refine((p) => Object.keys(p).length > 0, { message: "Patch must include at least one field" });

export const updateAdminShopVirtualProductContract = baseContract
  .route({ method: "PATCH", path: "/admin/shops/{customerId}/virtual-products/{virtualProductId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      virtualProductId: z.coerce.number().int().positive(),
      patch: VirtualProductPatchSchema,
    }),
  )
  .output(AdminVirtualProductDetailSchema);

// ---- shops.aiThreads ---------------------------------------------------
const ThreadRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  userName: z.string().nullable(),
  messageCount: z.number().int().min(0),
  models: z.array(z.string()),
  aiCostUsd: z.number().min(0),
  aiTotalTokens: z.number().int().min(0),
  aiCallCount: z.number().int().min(0),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const listAdminShopAiThreadsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/ai-threads" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      search: z.string().trim().max(100).optional(),
    }),
  )
  .output(z.object({ threads: z.array(ThreadRowSchema), total: z.number().int().min(0) }));

const ThreadMessageSchema = z.object({
  id: z.string(),
  message: z.unknown(),
  createdAt: z.string().datetime(),
});

export const getAdminShopAiThreadContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/ai-threads/{threadId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      threadId: z.string().min(1, "Required").max(64),
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
    }),
  )
  .output(
    z.object({
      thread: ThreadRowSchema,
      messages: z.array(ThreadMessageSchema),
    }),
  );

// ---- shops.suggestionRuns ------------------------------------------------
const SuggestionRunCountsSchema = z.object({
  total: z.number().int().min(0),
  active: z.number().int().min(0),
  approved: z.number().int().min(0),
  dismissed: z.number().int().min(0),
});

const SuggestionRunRowSchema = z.object({
  id: z.number(),
  triggerType: z.string(),
  status: z.string(),
  error: z.string().nullable(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  candidatesCount: z.number().int().min(0),
  agentSelectedCount: z.number().int().min(0),
  agentRejectedCount: z.number().int().min(0),
  counts: SuggestionRunCountsSchema,
  /** Number of this run's suggestions whose brief has a built funnel (BriefOutput of type FUNNEL). */
  builtFunnelsCount: z.number().int().min(0),
  /** AI chat thread id holding the agent's reasoning trace for this run, when one exists. */
  threadId: z.string().nullable(),
});

export const listAdminShopSuggestionRunsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/suggestion-runs" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      status: z.string().trim().max(50).optional(),
    }),
  )
  .output(z.object({ runs: z.array(SuggestionRunRowSchema), total: z.number().int().min(0) }));

const SuggestionBuiltFunnelSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  status: z.string(),
});

const SuggestionRunSuggestionSchema = z.object({
  suggestionId: z.number(),
  briefId: z.number(),
  title: z.string(),
  briefStatus: z.string(),
  isAiGenerated: z.boolean(),
  goal: z.string().nullable(),
  audience: z.string().nullable(),
  creativeDirection: z.string().nullable(),
  targetLaunchDate: z.string().datetime().nullable(),
  linkedAdEntities: z.unknown(),
  suggestionStatus: z.string(),
  playType: z.string().nullable(),
  sourceType: z.string().nullable(),
  suggestionCard: z.unknown().nullable(),
  sourceRefs: z.unknown().nullable(),
  evidenceSnapshot: z.unknown().nullable(),
  dismissedReason: z.string().nullable(),
  dismissedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  builtFunnel: SuggestionBuiltFunnelSchema.nullable(),
});

export const getAdminShopSuggestionRunContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/suggestion-runs/{runId}" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam.extend({ runId: z.coerce.number().int().positive() }))
  .output(
    z.object({
      run: SuggestionRunRowSchema.extend({
        candidatesSnapshot: z.unknown().nullable(),
        agentSelected: z.unknown().nullable(),
        agentRejected: z.unknown().nullable(),
      }),
      suggestions: z.array(SuggestionRunSuggestionSchema),
    }),
  );

// Flat, cross-run view of every brief for a shop — both AI-suggested and manually authored — for
// browsing/debugging without having to open each run individually. `suggestion` is null for briefs
// that were never proposed by the suggestion engine (manual authoring, or AI-assisted via chat
// outside the suggestion flow).
const BriefListSuggestionInfoSchema = z.object({
  suggestionId: z.number(),
  runId: z.number(),
  suggestionStatus: z.string(),
  playType: z.string().nullable(),
  sourceType: z.string().nullable(),
  suggestionCard: z.unknown().nullable(),
  sourceRefs: z.unknown().nullable(),
  evidenceSnapshot: z.unknown().nullable(),
  dismissedReason: z.string().nullable(),
  dismissedAt: z.string().datetime().nullable(),
});

const BriefListItemSchema = z.object({
  briefId: z.number(),
  title: z.string(),
  briefStatus: z.string(),
  isAiGenerated: z.boolean(),
  goal: z.string().nullable(),
  audience: z.string().nullable(),
  creativeDirection: z.string().nullable(),
  targetLaunchDate: z.string().datetime().nullable(),
  linkedAdEntities: z.unknown(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  builtFunnel: SuggestionBuiltFunnelSchema.nullable(),
  suggestion: BriefListSuggestionInfoSchema.nullable(),
});

const BriefOriginFilterSchema = z.enum(["AI_SUGGESTED", "MANUAL"]);

export const listAdminShopBriefsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/briefs" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam.extend({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      offset: z.coerce.number().int().min(0).default(0),
      origin: BriefOriginFilterSchema.optional(),
      suggestionStatus: z.string().trim().max(50).optional(),
      search: z.string().trim().max(100).optional(),
    }),
  )
  .output(
    z.object({
      briefs: z.array(BriefListItemSchema),
      total: z.number().int().min(0),
    }),
  );

const AdminShopSuggestionStatsSchema = z.object({
  totalRuns: z.number().int().min(0),
  failedRuns: z.number().int().min(0),
  totalSuggestions: z.number().int().min(0),
  activeCount: z.number().int().min(0),
  approvedCount: z.number().int().min(0),
  dismissedCount: z.number().int().min(0),
  approvalRate: z.number().min(0).max(1).nullable(),
  dismissalRate: z.number().min(0).max(1).nullable(),
  dismissedReasonBreakdown: z.array(z.object({ reason: z.string(), count: z.number().int().min(0) })),
  manualBriefsCount: z.number().int().min(0),
  aiGeneratedBriefsCount: z.number().int().min(0),
  briefsWithBuiltFunnelCount: z.number().int().min(0),
});

export const getAdminShopSuggestionStatsContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/suggestion-runs/stats" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(customerIdParam)
  .output(AdminShopSuggestionStatsSchema);

// ---- shops.auditLog --------------------------------------------------
export const listAdminShopAuditLogContract = baseContract
  .route({ method: "GET", path: "/admin/shops/{customerId}/audit-log" })
  .meta({ accessLevel: ACCESS_LEVEL.ADMIN, permissions: { basic: ["manage"] } })
  .input(
    customerIdParam
      .extend({
        limit: z.coerce.number().int().min(1).max(200).optional(),
        offset: z.coerce.number().int().min(0).default(0),
      })
      .strict(),
  )
  .output(
    z.object({
      entries: z.array(
        z.object({
          id: z.number(),
          action: z.string(),
          entityType: z.string(),
          entityId: z.string(),
          userId: z.string(),
          actorEmail: z.string().nullable(),
          details: z.unknown(),
          createdAt: z.string().datetime(),
        }),
      ),
      total: z.number().int().min(0),
    }),
  );

// ---- existing billing endpoints (unchanged) ----------------------------
export const createBillingPlanContract = baseContract
  .route({ method: "POST", path: "/admin/billing/plans" })
  .meta({
    accessLevel: ACCESS_LEVEL.ADMIN,
    permissions: { basic: ["manage"] },
  })
  .input(
    z.object({
      shopDomain: z.string().optional(),
      name: z.string(),
      description: z.string(),
      isActive: z.boolean(),
      isTest: z.boolean(),
      price: z.number().min(0),
      currencyCode: z.string().optional(),
      interval: BillingCycleSchema.optional().default("month"),
      trialDays: z.number().int().min(0).optional(),
      entitlements: z.array(BillingEntitlementSchema).optional(),
    }),
  )
  .output(
    z.object({
      id: z.number(),
    }),
  );

export const calculateBillingUsageContract = baseContract
  .route({ method: "POST", path: "/admin/billing/calculate" })
  .meta({
    accessLevel: ACCESS_LEVEL.ADMIN,
    permissions: { basic: ["manage"] },
  })
  .input(
    ManualBillingCalculationInputSchema.extend({
      shopDomain: z.string(),
    }),
  );

// ---- router shape -------------------------------------------------------
export const adminRouterContract = {
  analytics: adminAnalyticsRouterContract,
  financial: adminFinancialRouterContract,
  tools: {
    inspiration: adminInspirationRouterContract,
    metaAttribution: adminMetaAttributionRouterContract,
    storefront: adminStorefrontRouterContract,
  },
  users: {
    list: listAdminUsersContract,
    get: getAdminUserContract,
    update: updateAdminUserContract,
    repairOrganizationMembership: repairAdminUserOrganizationMembershipContract,
  },
  shops: {
    list: getAdminShopsContract,
    listAll: getAllAdminShopsContract,
    get: getAdminShopContract,
    create: createAdminShopContract,
    update: updateAdminShopContract,
    domains: {
      create: createAdminShopDomainContract,
    },
    customHeadlessConfigs: {
      list: listAdminShopCustomHeadlessConfigsContract,
      get: getAdminShopCustomHeadlessConfigContract,
      update: updateAdminShopCustomHeadlessConfigContract,
      updateGeneral: updateAdminShopGeneralCustomHeadlessConfigContract,
    },
    storefrontFallbacks: {
      list: listAdminShopStorefrontFallbacksContract,
      create: createAdminShopStorefrontFallbackContract,
      update: updateAdminShopStorefrontFallbackContract,
      delete: deleteAdminShopStorefrontFallbackContract,
    },
    brandDesign: {
      get: getAdminShopBrandDesignContract,
      start: startAdminShopBrandDesignContract,
    },
    integrations: { get: getAdminShopIntegrationsContract },
    shopifyWebhooks: { remove: removeAdminShopifyWebhooksContract },
    members: { list: listAdminShopMembersContract },
    billing: {
      get: getAdminShopBillingContract,
      usageCharges: adminShopUsageChargesContract,
      update: updateAdminShopBillingContract,
      plans: {
        create: createAdminShopBillingPlanContract,
        update: updateAdminShopBillingPlanContract,
      },
    },
    config: {
      flags: {
        get: getAdminShopFlagsContract,
        update: updateAdminShopFlagsContract,
      },
      abDefaults: {
        get: getAdminShopAbDefaultsContract,
        update: updateAdminShopAbDefaultsContract,
      },
    },
    products: {
      list: listAdminShopProductsContract,
      get: getAdminShopProductContract,
      create: createAdminShopProductContract,
      update: updateAdminShopProductContract,
    },
    aiThreads: {
      list: listAdminShopAiThreadsContract,
      get: getAdminShopAiThreadContract,
    },
    virtualProducts: {
      list: listAdminShopVirtualProductsContract,
      get: getAdminShopVirtualProductContract,
      update: updateAdminShopVirtualProductContract,
    },
    funnels: {
      list: listAdminShopFunnelsContract,
      get: getAdminShopFunnelContract,
      updatePageJsx: updateAdminShopFunnelPageJsxContract,
      getVersion: getAdminShopFunnelVersionContract,
      copyTo: copyAdminShopFunnelContract,
      copyStatus: copyStatusAdminShopFunnelContract,
    },
    auditLog: { list: listAdminShopAuditLogContract },
    suggestionRuns: {
      list: listAdminShopSuggestionRunsContract,
      get: getAdminShopSuggestionRunContract,
      stats: getAdminShopSuggestionStatsContract,
    },
    briefs: {
      list: listAdminShopBriefsContract,
    },
  },
  billing: {
    createPlan: createBillingPlanContract,
    calculateUsage: calculateBillingUsageContract,
  },
  systemConfig: {
    entries: adminSystemConfigRouterContract,
    aiModels: {
      list: listAdminAiModelSystemConfigContract,
      update: updateAdminAiModelSystemConfigContract,
    },
    brain: {
      updateModels: updateAdminBrainSystemConfigContract,
    },
  },
};
